import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { SchoolProvider } from './context/SchoolContext';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import ImpactSection from './components/ImpactSection';
import InfoSection from './components/InfoSection';
import RepresentativeSection from './components/RepresentativeSection';
import Footer from './components/Footer';
import ActionItems from './components/ActionItems';
import './App.css';

function MainContent({ selectedSchool, setSelectedSchool, impactRef, appRef, representativeRef, sectionsRef }) {
  // Configure intersection observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Smooth scroll only if more than 30% of element is visible
          const visibleRatio = entry.intersectionRatio;
          if (visibleRatio > 0.3) {
            entry.target.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }
        }
      });
    }, observerOptions);

    // Observe all sections
    sectionsRef.current.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, [sectionsRef]);

  // Scroll to impact section when school is selected
  useEffect(() => {
    if (selectedSchool && impactRef.current) {
      setTimeout(() => {
        impactRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [selectedSchool, impactRef]);

  // Handle initial scroll to representative section
  useEffect(() => {
    const shouldScroll = window.location.hash === "#call" || 
                        window.location.pathname === "/call";
    if (shouldScroll && representativeRef.current) {
      setTimeout(() => {
        representativeRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [representativeRef]);

  return (
    <div className="app" ref={appRef}>
      {/* Action Items Popup */}
      <ActionItems position="popup" />
      
      <section 
        className="section header-section" 
        ref={el => sectionsRef.current[0] = el}
      >
        <Header />
        <SearchBar onSelect={setSelectedSchool} />
      </section>
      
      {selectedSchool && (
        <section 
          className="section impact-section" 
          ref={el => {
            impactRef.current = el;
            sectionsRef.current[1] = el;
          }}
        >
          <ImpactSection school={selectedSchool} />
        </section>
      )}

      <section 
        className="section info-section" 
        ref={el => sectionsRef.current[2] = el}
      >
        <InfoSection />
      </section>

      <section 
        className="section representative-section" 
        ref={el => {
          representativeRef.current = el;
          sectionsRef.current[3] = el;
        }}
      >
        <RepresentativeSection selectedSchool={selectedSchool} />
      </section>

      <section 
        className="section footer-section" 
        ref={el => sectionsRef.current[4] = el}
      >
        <Footer />
      </section>
    </div>
  );
}

function TestifyRedirect() {
  useEffect(() => {
    window.location.href = 'https://comments.house.texas.gov/home?c=c400';
  }, []);
  return null;
}

function App() {
  const [selectedSchool, setSelectedSchool] = useState(null);
  const impactRef = useRef(null);
  const appRef = useRef(null);
  const representativeRef = useRef(null);
  const sectionsRef = useRef([]);

  // Configure intersection observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Smooth scroll only if more than 30% of element is visible
          const visibleRatio = entry.intersectionRatio;
          if (visibleRatio > 0.3) {
            entry.target.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }
        }
      });
    }, observerOptions);

    // Observe all sections
    sectionsRef.current.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  // Scroll to impact section when school is selected
  useEffect(() => {
    if (selectedSchool && impactRef.current) {
      setTimeout(() => {
        impactRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [selectedSchool]);

  // Handle initial scroll to representative section
  useEffect(() => {
    const shouldScroll = window.location.hash === "#call" || 
                        window.location.pathname === "/call";
    if (shouldScroll && representativeRef.current) {
      setTimeout(() => {
        representativeRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  return (
    <Router>
      <SchoolProvider>
        <Routes>
          <Route path="/testify" element={<TestifyRedirect />} />
          <Route path="/" element={
            <MainContent 
              selectedSchool={selectedSchool}
              setSelectedSchool={setSelectedSchool}
              impactRef={impactRef}
              appRef={appRef}
              representativeRef={representativeRef}
              sectionsRef={sectionsRef}
            />
          } />
        </Routes>
      </SchoolProvider>
    </Router>
  );
}

export default App;
